import { Injectable } from '@angular/core';
import { getStatusText, Status, USER_TYPE_LABELS, UserTypeId } from '@shared/models/enums';
import type {
  DialCodeRead,
  PersonnelListItem,
  PersonnelMe,
  PersonnelRead,
  PhoneInput,
  PhoneRef,
  RoleCategoryRead,
} from '@core/api/generated';
import type { UserPersonnel } from '@core/auth/auth.service';
import {
  PersonnelListData,
  PersonnelProfileData,
  PERSONNEL_STATUSES,
  isHeldGrant,
  mapPersonnelStatusCode,
} from '../models/personnel.model';
import { MultiSelectOption } from '@shared/forms/multi-select/multi-select.component';
import { PhoneNumber } from '@shared/ui/phone-modal/phone-modal.component';

/** `PhoneRef` plus the optional `dial_code_id` the backend may send — not yet in the OpenAPI spec. */
type PhoneRefWithDialCodeId = PhoneRef & { dial_code_id?: number | null };

export interface RoleOption {
  code: string;
  label: string;
  categoryCode: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class PersonnelMapper {
  setPersonnelListRowData(rows: PersonnelListItem[], translateData: any): PersonnelListData[] {
    return rows.map((personnel): PersonnelListData => {
      const statusLabel = mapPersonnelStatusCode(personnel.status);
      const statusValue = PERSONNEL_STATUSES.find((s) => s.label === statusLabel)?.value ?? 1;
      const roleCodes = (personnel.roles ?? []).filter(isHeldGrant).map((r) => r.code);

      return {
        id: personnel.id,
        org_id: personnel.organization.id,
        per_status: { id: statusValue, stat_name: getStatusText(statusValue as Status, translateData) },
        user_type_id: this.getUserTypeId(roleCodes),
        user_type_display: this.getUserType(roleCodes),
        full_name: `${personnel.name1 ?? ''} ${personnel.surn1 ?? ''}`.trim(),
        map_address: this.getAddressDisplay(personnel),
        statusText: getStatusText(statusValue as Status, translateData),
        org_denom: `${personnel.organization.denom || ''} ${personnel.organization.name || ''} ${personnel.organization.tax || ''}`
          .toLowerCase()
          .trim(),
        org_denom_display: personnel.organization.denom,
        org_name: personnel.organization.name ?? '',
        org_tax: personnel.organization.tax ?? '',
        roles: roleCodes,
        infrastructures: personnel.infrastructure_object?.count ?? 0,
        infrastructures_petitioned: personnel.infrastructure_object?.pending ?? 0,
        equipment: personnel.aircraft_object?.count ?? 0,
        equipment_petitioned: personnel.aircraft_object?.pending ?? 0,
      };
    });
  }

  /**
   * Adapts the v2 self-profile (`PersonnelMe` from GET /api/v1/personnel/me)
   * into the legacy `UserPersonnel` shape that `AuthService.setUserValue` stores
   * and the whole app shell reads (`org_id`, `org_name`, `user_type_id`,
   * `rolenameArray`, `lang_id`, ...). This is the bootstrap mapping that lets the
   * shell boot on the v2 gateway instead of the legacy `/approutes/personnel/me`.
   */
  toAuthUser(me: PersonnelMe): UserPersonnel {
    const roleCodes = me.role_codes ?? (me.roles ?? []).map((r) => r.code);
    const displayName = `${me.name1 ?? ''} ${me.surn1 ?? ''}`.trim();
    return {
      id: me.id,
      per_id: me.id,
      org_id: me.organization.id,
      org_name: me.organization_name ?? me.organization.name ?? me.organization.denom,
      per_name: me.name1 ?? '',
      per_surname: me.surn1 ?? '',
      per_user: displayName || me.email || '',
      user_type_id: this.getAuthUserTypeId(me, roleCodes),
      rolenameArray: roleCodes,
      lang_id: me.language?.id ?? 0,
      preferred_url: me.preferences?.preferred_url ?? null,
    };
  }

  /**
   * The v2 `PersonnelMe` exposes `is_system_admin`/`is_org_admin` as the
   * authoritative admin flags; prefer them, falling back to the `SADM`/`OADM`
   * role codes (same convention as the list-row `getUserTypeId`).
   */
  private getAuthUserTypeId(me: PersonnelMe, roleCodes: string[]): UserTypeId {
    if (me.is_system_admin) return UserTypeId.SUPER;
    if (me.is_org_admin) return UserTypeId.ADMIN;
    if (roleCodes.includes('SADM')) return UserTypeId.SUPER;
    if (roleCodes.includes('OADM')) return UserTypeId.ADMIN;
    return UserTypeId.USER;
  }

  /**
   * Role code -> display description, sourced from this feature's own i18n
   * dictionary (`translateData[code.toLowerCase()]`) — same as before this
   * migration; role descriptions were never fetched from the backend, only role
   * codes/names were, so this needs no new endpoint.
   */
  getRoleDescription(code: string, translateData: any): string {
    return translateData?.[code.toLowerCase()] ?? code;
  }

  /**
   * `SADM`/`OADM` are the only role codes this codebase already assumes exist
   * (see infrastructure's old categoryOrder list and the generated models' own
   * doc comment on `OADM`) — flagged as a judgment call, not confirmed against
   * a full role catalog dump.
   */
  private getUserTypeId(roleCodes: string[]): UserTypeId {
    if (roleCodes.includes('SADM')) return UserTypeId.SUPER;
    if (roleCodes.includes('OADM')) return UserTypeId.ADMIN;
    return UserTypeId.USER;
  }

  private getUserType(roleCodes: string[]): string {
    return USER_TYPE_LABELS[this.getUserTypeId(roleCodes)] || 'N/A';
  }

  private getAddressDisplay(row: PersonnelListItem): string {
    return (
      (row.address?.municipality ?? '-') +
      ' | ' +
      (row.address?.division ?? '-') +
      ' | ' +
      (row.address?.territory ?? '-')
    );
  }

  // ==== personnel profile ====
  /**
   * Adapts the generated `PersonnelRead` into the old `PersonnelProfileData`
   * shape (`per_name1`, `org_id`, `roles: {role,validated}[]`, `per_adm`, ...) —
   * `personnel-profile.ts` stores this in `PersonnelStore` and reuses it both
   * fresh from the API and back out of the store (`cancelEdit()`), so keeping
   * one stable shape here avoids touching every read site across the page.
   */
  toPersonnelProfileData(data: PersonnelRead): PersonnelProfileData {
    const statusLabel = mapPersonnelStatusCode(data.status);
    const statusValue = PERSONNEL_STATUSES.find((s) => s.label === statusLabel)?.value ?? 1;
    return {
      id: data.id,
      per_id: String(data.id),
      per_user: data.keycloak_sub ?? '',
      per_name1: data.name1 ?? '',
      per_name2: data.name2 ?? '',
      per_surn1: data.surn1 ?? '',
      per_surn2: data.surn2 ?? '',
      per_card: '',
      per_adm: data.is_org_admin ? 1 : 0,
      org_id: data.organization.id,
      per_dep: data.department ?? '',
      per_pos: data.position ?? '',
      lang_id: 0,
      per_email: data.email ?? '',
      per_mobil: data.mobile?.number ?? '',
      per_phone: data.phone?.number ?? '',
      mobile: data.mobile ?? null,
      phone: data.phone ?? null,
      per_indica: data.indica ?? '',
      per_adr_1: 0,
      per_note: data.note ?? '',
      per_status: { id: statusValue, stat_name: statusLabel },
      user_type_id: 0,
      per_company: data.company ?? '',
      createdAt: '',
      updatedAt: '',
      roles: (data.roles ?? [])
        .filter(isHeldGrant)
        .map((r) => ({ role: r.code, validated: r.status === 'ACT' })),
      address: {
        id: 0,
        adr_addst: data.address?.street ?? '',
        adr_addpc: data.address?.post_code ?? '',
        adr_addtwn: data.address?.municipality ?? '',
        adr_addprv: data.address?.division ?? '',
        adr_addterr: data.address?.territory ?? '',
        loc_municipality_id: data.address?.municipality_id ?? null,
        loc_division_id: data.address?.division_id ?? null,
        loc_territory_id: data.address?.territory_id ?? null,
        loc_region_id: null,
      },
      org_name: data.organization.name ?? data.organization.denom,
    };
  }

  getPersonnelDataFormStructure(
    personnelData: PersonnelProfileData,
    dialCodes: DialCodeRead[] = [],
  ): any {
    return {
      // extra info
      id: personnelData.id,
      org_id: personnelData.org_id,
      // identification
      name1: personnelData.per_name1,
      name2: personnelData.per_name2,
      surname1: personnelData.per_surn1,
      surname2: personnelData.per_surn2,
      company: personnelData.per_company,
      department: personnelData.per_dep,
      jobPosition: personnelData.per_pos,
      status:
        PERSONNEL_STATUSES.find((s) => s.value === personnelData.per_status.id)?.label ??
        personnelData.per_status.stat_name,

      // contact
      email: personnelData.per_email,
      mobile: this.mapPhoneRef(personnelData.mobile, dialCodes, 'M'),
      phone: this.mapPhoneRef(personnelData.phone, dialCodes, 'T'),
      contactInstructions: personnelData.per_indica,

      // organization
      adminOrganization: !!personnelData.per_adm,
      userRol: personnelData.roles?.map((el) => el.role) ?? [],

      // adress
      address: personnelData.address.adr_addst,
      postalCode: personnelData.address.adr_addpc,
      city: personnelData.address.adr_addtwn,
      province: personnelData.address.adr_addprv,
      country: personnelData.address.adr_addterr,
      locMunicipalityId: personnelData.address.loc_municipality_id,
      locDivisionId: personnelData.address.loc_division_id,
      locTerritoryId: personnelData.address.loc_territory_id,
      locRegionId: personnelData.address.loc_region_id,

      // preferences
      timeZone: 'UTC',

      // notes
      notes: personnelData.per_note,
    };
  }

  getOrganizationDataFormStructure(organizationData: any): any {
    const denom = organizationData.denom ?? '-';
    const name = organizationData.name ?? '-';
    const tax = organizationData.tax ?? '-';
    const organizationName = `${denom} [${name}/${tax}]`;
    return {
      organization: organizationName,
    };
  }

  /**
   * Builds the autosave payload. `mobile`/`phone` are now structured
   * `PhoneNumber | null` values (see `getPersonnelDataFormStructure`), mapped here
   * into `PhoneInput{number, dial_code_id}` — `undefined` is omitted (leave the
   * field untouched), `null` is sent explicitly (clear the association), and an
   * object associates/updates the phone.
   */
  toPersonnelUpdatePayload(formValue: any): any {
    return this.omitUndefined({
      name1: formValue.name1,
      name2: formValue.name2,
      surn1: formValue.surname1,
      surn2: formValue.surname2,
      company: formValue.company,
      department: formValue.department,
      position: formValue.jobPosition,
      email: formValue.email,
      mobile: this.buildPhoneInput(formValue.mobile),
      phone: this.buildPhoneInput(formValue.phone),
      indica: formValue.contactInstructions,
      note: formValue.notes,
      address: this.omitUndefined({
        street: formValue.address,
        post_code: formValue.postalCode,
        municipality: formValue.city,
        division: formValue.province,
        // The backend resolves the country only from its id: AddressInput has no territory text.
        municipality_id: formValue.locMunicipalityId,
        division_id: formValue.locDivisionId,
        territory_id: formValue.locTerritoryId,
      }),
    });
  }

  private mapPhoneRef(
    phone: PhoneRefWithDialCodeId | null | undefined,
    dialCodes: DialCodeRead[],
    type: 'M' | 'T',
  ): PhoneNumber | null {
    if (!phone) return null;

    const dialCode =
      dialCodes.find((d) => d.id === phone.dial_code_id) ??
      dialCodes.find((d) => (d.dial_code ?? '').trim() === (phone.dial_code ?? '').trim());

    return {
      type,
      dialCodeId: dialCode?.id ?? phone.dial_code_id ?? 0,
      territory: dialCode?.territory ?? '',
      number: phone.number ?? '',
    };
  }

  private buildPhoneInput(
    phone: PhoneNumber | null | undefined,
  ): PhoneInput | null | undefined {
    if (phone === undefined) return undefined;
    if (!phone || !phone.number?.trim()) return null;

    return {
      number: phone.number.trim(),
      dial_code_id: phone.dialCodeId,
    };
  }

  toPreferencesPayload(formValue: any): any {
    if (formValue.defaultPage === undefined) return {};
    const categoriesWithInfrastructure = ['document', 'monitor'];
    const page = formValue.defaultPage;
    let preferred_url = `/${page}`;
    if (categoriesWithInfrastructure.includes(page) && formValue.defaultPageInfrastructure) {
      preferred_url += `/${formValue.defaultPageInfrastructure}`;
    }
    return { preferred_url };
  }

  private omitUndefined<T extends Record<string, any>>(obj: T): Partial<T> {
    return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as Partial<T>;
  }

  getPreferenceDefaultPage(preferenceData: { preferred_url?: string | null }): any {
    const defaultPages = this.getDefaultPageData(preferenceData);
    return {
      defaultPage: defaultPages[0] ?? 'default',
      defaultPageInfrastructure: defaultPages.length > 1 ? defaultPages[1] : '',
    };
  }

  private getDefaultPageData(data: { preferred_url?: string | null }): string[] {
    return (data.preferred_url ?? '').split('/').filter((el) => el.trim());
  }

  getPersonnelProfileFormStructure(data: PersonnelProfileData): any {
    return {
      name1: data.per_name1,
      name2: data.per_name2,
      surname1: data.per_surn1,
      surname2: data.per_surn2,
      company: data.per_company,
      department: data.per_dep,
      jobPosition: null,
      status:
        PERSONNEL_STATUSES.find((s) => s.value === data.per_status.id)?.label ??
        data.per_status.stat_name,
      email: data.per_email,
      mobile: data.per_mobil,
      phone: data.per_phone,
      contactInstructions: data.per_indica,
      organization: data.org_name,
      adminOrganization: data.per_adm,
      userRole: data.roles,
      address: data.address,
      postalCode: data.per_pos,
      city: null,
      state: data.address.adr_addst,
      country: null,
      timeZone: 'UTC',
      defaultPage: null,
      notes: data.per_note,
      infrastructure: null,
      aircraft: null,
    };
  }

  // ====== role picker (organization role catalog) ======
  /**
   * Flattens the new `RoleCategoryRead[]` (grouped by org category) into a flat
   * option list — the picker doesn't need the grouping structure itself, just
   * codes with a category label. `cat_code`/`code` are short codes, not
   * translated text (same caveat as infrastructure's org-categories).
   */
  toRoleOptions(categories: RoleCategoryRead[], translateData: any): RoleOption[] {
    return categories.flatMap((category) =>
      (category.roles ?? []).map((role) => {
        const description = this.getRoleDescription(role.code, translateData);
        return {
          code: role.code,
          label: description === role.code ? role.code : `${role.code} - ${description}`,
          categoryCode: category.cat_code ?? null,
        };
      }),
    );
  }

  // ====== personnel preferences ======
  infrastructureMapperMultiSelectOption(data: { id: number; name?: string | null }): MultiSelectOption {
    return {
      label: data.name ?? String(data.id),
      value: data.id,
    };
  }
}
